import React, { useEffect, useState } from "react";
import { PageHeader, Card, Button, Input, Spinner, Badge } from "@/components/ui";
import { endpoints, apiError } from "@/lib/api";
import EntityProfile, { SourceLabel } from "@/components/EntityProfile";
import { Search, ChevronRight } from "lucide-react";

function ProviderStrip({ providers }) {
  if (!providers) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/45" data-testid="intel-provider-strip">
      Lookup order:
      {providers.order.map((p, i) => (
        <span key={p} className="flex items-center gap-1">
          <Badge tone={providers.configured[p] ? "green" : "neutral"}>{p}{providers.configured[p] ? "" : " · off"}</Badge>
          {i < providers.order.length - 1 && <ChevronRight size={11} />}
        </span>
      ))}
    </div>
  );
}

export default function CompanyIntel() {
  const [f, setF] = useState({ name: "", country: "", vat: "", reg: "" });
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [providers, setProviders] = useState(null);
  const [recent, setRecent] = useState([]);
  const [open, setOpen] = useState(null);

  const loadRecent = () => endpoints.entities("").then((r) => setRecent(r.data.slice(0, 12))).catch(() => {});
  useEffect(() => { endpoints.intelProviders().then((r) => setProviders(r.data)).catch(() => {}); loadRecent(); }, []);

  const search = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setRes(null);
    try { const r = await endpoints.intelLookup(f); setRes(r.data); loadRecent(); } catch (ex) { setErr(apiError(ex)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      <PageHeader eyebrow="Company Intelligence" title={<>Search any <span className="italic text-gold">company</span>.</>}
        description="Global Database first, Cerved for Italian companies, then web research. Every result is stored as a canonical, provenance-tracked entity." testid="intel-page" />
      <Card hover={false}>
        <form onSubmit={search} className="grid md:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-2" data-testid="intel-search-form">
          <Input placeholder="Company name" value={f.name} onChange={set("name")} data-testid="intel-name" />
          <Input placeholder="Country" value={f.country} onChange={set("country")} data-testid="intel-country" />
          <Input placeholder="VAT ID" value={f.vat} onChange={set("vat")} data-testid="intel-vat" />
          <Input placeholder="Reg. number" value={f.reg} onChange={set("reg")} data-testid="intel-reg" />
          <Button type="submit" disabled={busy || !(f.name || f.vat || f.reg)} data-testid="intel-search-btn">{busy ? <Spinner size={13} /> : <Search size={13} />} Search</Button>
        </form>
        <div className="mt-3"><ProviderStrip providers={providers} /></div>
      </Card>

      {err && <div className="text-red-300 text-sm" data-testid="intel-error">{err}</div>}
      {res && (
        <Card hover={false} data-testid="intel-result-card">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div className="font-display text-2xl">{res.profile?.data?.name || f.name}</div>
            <SourceLabel source={res.source} testid="intel-result-source" />
          </div>
          {!res.ok && <div className="text-white/50 text-sm" data-testid="intel-no-match">No provider returned a match.</div>}
          <div className="text-[11px] text-white/40 mb-4" data-testid="intel-sources-tried">
            Tried: {(res.sources_tried || []).map((t) => `${t.source} ${t.ok ? "✓" : "✗"}`).join(" · ")}
          </div>
          <EntityProfile entity={res.profile} testid="intel-entity" />
        </Card>
      )}

      <Card hover={false}>
        <div className="overline mb-3">Recently resolved entities</div>
        <div className="divide-y divide-white/5" data-testid="intel-recent-list">
          {recent.map((e) => (
            <div key={e.id}>
              <button onClick={() => setOpen(open === e.id ? null : e.id)} className="w-full flex items-center justify-between py-2.5 text-left hover:text-gold transition-colors" data-testid={`intel-recent-${e.id.slice(0, 8)}`}>
                <span className="text-[13px]">{e.data?.name} <span className="text-white/35 text-[11px]">{e.data?.country}</span></span>
                <span className="flex gap-1">{(e.sources || []).map((s) => <SourceLabel key={s} source={s} />)}</span>
              </button>
              {open === e.id && <div className="pb-4"><EntityProfile entity={e} testid={`intel-recent-profile-${e.id.slice(0, 8)}`} /></div>}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
